import { CDFI_CATEGORIES } from '../../constants';
import type {
  GaugeWithTableColorType,
  GaugeWithTableTableRowType,
} from '../types/components';

export const cdfiStatsName = {
  LOAN: 'loan',
  DOLLAR: 'dollar',
  ACCOUNT_HOLDERS: 'accountHolders',
} as const;

export type CdfiStatsNameType =
  (typeof cdfiStatsName)[keyof typeof cdfiStatsName];

export const cdfiStatsLabelMap = {
  [cdfiStatsName.LOAN]: 'CDFI Target Market Loans (by Number of Loans)',
  [cdfiStatsName.DOLLAR]: 'CDFI Target Market Loans (by Dollar Amount)',
  [cdfiStatsName.ACCOUNT_HOLDERS]:
    'Number of Unique Depository Account Holders',
};

export const cdfiStatsPrimaryMetricLabelMap = {
  [cdfiStatsName.LOAN]: 'Total Number of Loans',
  [cdfiStatsName.DOLLAR]: 'Total Dollar Amount of Loans',
  [cdfiStatsName.ACCOUNT_HOLDERS]: 'Total members',
};

export const cdfiStatsSecondaryMetricLabelMap = {
  [cdfiStatsName.LOAN]: 'Number of CDFI Target Market loans',
  [cdfiStatsName.DOLLAR]: 'Dollar amount of CDFI Target Market loans',
  [cdfiStatsName.ACCOUNT_HOLDERS]: 'CDFI qualified members',
};

export const cdfiStatsHeaderMap = {
  [cdfiStatsName.LOAN]: ['Category', 'TM Loan', '# of Loans', 'Percentage'],
  [cdfiStatsName.DOLLAR]: [
    'Category',
    'TM Loan',
    'Dollar amount',
    'Percentage',
  ],
  [cdfiStatsName.ACCOUNT_HOLDERS]: [
    'Category',
    'TM Loan',
    '# of Members',
    'Percentage',
  ],
};

//  display to server name mapping
export const cdfiCategoryDisplayToServerMap = Object.values(
  CDFI_CATEGORIES
).reduce((acc, { display, server }) => {
  acc[display] = server;
  return acc;
}, {} as Record<string, string>);

// server to display name mapping
export const cdfiCategoryServerToDisplayMap = Object.values(
  CDFI_CATEGORIES
).reduce((acc, { display, server }) => {
  acc[server] = display;
  return acc;
}, {} as Record<string, string>);

// sort the table data to show "Yes" categories first, then by name
export const sortCdfiCategories = (categories: GaugeWithTableTableRowType[]) =>
  categories.sort((a, b) => {
    if (a.status === b.status) {
      // Keep OTP category at the end of the "Yes" categories
      if (a.status === 'Yes') {
        if (a.name.includes('OTP')) return 1;
        if (b.name.includes('OTP')) return -1;
      }
      return a.name.localeCompare(b.name);
    }
    return a.status === 'Yes' ? -1 : 1;
  });

// accepts percentage and color grades
export const mapCdfiStatsColor = (
  percentage: number,
  [firstStep, secondStep]: [number, number?]
): GaugeWithTableColorType => {
  let color: GaugeWithTableColorType;

  if (percentage > firstStep) {
    color = 'green';
  } else if (secondStep && percentage > secondStep) {
    color = 'yellow';
  } else {
    color = 'red';
  }

  return color;
};

export interface CdfiStatus {
  isCertified: boolean;
  message: string;
  membershipMessage: string;
  isMembershipRateRendered: boolean;
}

export const getCdfiStatus = (
  loanPercentage: number,
  dollarPercentage: number,
  accountPercentage: number,
  isAIRESFileUploaded: boolean,
  isEmptyState: boolean
): {
  isCertified: boolean;
  message: string;
  membershipMessage: string;
  isMembershipRateRendered: boolean;
} => {
  if (loanPercentage >= 60 && dollarPercentage >= 60) {
    return {
      isCertified: false,
      message:
        'TM lending requirement met, Financial Services option not necessary',
      membershipMessage: 'Financial Services option not necessary',
      isMembershipRateRendered: false,
    };
  }

  if (
    ((loanPercentage >= 60 && dollarPercentage >= 50) ||
      (loanPercentage >= 50 && dollarPercentage >= 60)) &&
    !isAIRESFileUploaded
  ) {
    return {
      isCertified: false,
      message:
        'TM lending requirement not met, but Financial Services option is available - AIRES file upload needed',
      membershipMessage: 'AIRES Files upload needed',
      isMembershipRateRendered: true,
    };
  }

  if (
    ((loanPercentage >= 60 && dollarPercentage >= 50) ||
      (loanPercentage >= 50 && dollarPercentage >= 60)) &&
    isAIRESFileUploaded
  ) {
    return {
      isCertified: true,
      message: 'TM lending requirement met with the Financial Services option',
      membershipMessage: '',
      isMembershipRateRendered: true,
    };
  }

  if (
    (loanPercentage <= 60 && dollarPercentage <= 50) ||
    (loanPercentage <= 50 && dollarPercentage <= 60)
  ) {
    return {
      isCertified: false,
      message: 'less that 50',
      membershipMessage: 'Not available',
      isMembershipRateRendered: false,
    };
  }

  return {
    isCertified: false,
    message: '',
    membershipMessage: 'Not available',
    isMembershipRateRendered: false,
  };
};
